import * as THREE from 'three';
import { getPlaneIntersected } from '../../js';
import {
  LineIntersType,
  RoomObjectSurface,
  RoomType,
  SelectSupportsType,
  SupportMaterialsType,
  SupportsGroupType,
} from '../../types';
import { ConstructorSupportsProps, MaxMinPointsProps, PointIntersType } from './types';
import { addSupportMesh, getMaterialsSupports, getSupportGeometry } from './helpers';

export class SupportsGroup extends THREE.Group implements SupportsGroupType {
  static raycaster = new THREE.Raycaster();
  static radiusBaseSupport = 0.1;
  static ray = new THREE.Ray();
  static directBottom = new THREE.Vector3(0, -1, 0);
  static directTop = new THREE.Vector3(0, 1, 0);
  static geometrySupport = getSupportGeometry();
  static materialsSupport: SupportMaterialsType | null = getMaterialsSupports();
  protected linesInters: Array<LineIntersType> = [];

  contour: Array<THREE.Vector3>;
  rooms: Array<Array<number>>;
  roomsMeshes: Array<THREE.Mesh | RoomType> = [];
  planesZeroIntersected: Array<THREE.Mesh> = [];
  planeIntersBottom: THREE.Mesh | null = null;
  offsetX;
  offsetZ;
  customerOffsetX;
  customerOffsetZ;
  depthTile;
  selectModificationSupport;
  selectSupports: {
    [key: string]: SelectSupportsType;
  } | null = null;
  directionBuildDeg;
  compensatingLining = 0;

  constructor({
    contour,
    rooms,
    depthTile,
    offsetX,
    offsetZ,
    customerOffsetZ,
    customerOffsetX,
    roomsObjects,
    selectModificationSupport,
    directionBuildDeg,
  }: ConstructorSupportsProps) {
    super();
    this.name = 'supports_group';
    this.contour = contour;
    this.rooms = rooms || [];
    this.depthTile = depthTile || 0;
    this.offsetX = offsetX || 0.6;
    this.offsetZ = offsetZ || 0.6;
    this.customerOffsetX = customerOffsetX || 0;
    this.customerOffsetZ = customerOffsetZ || 0;
    this.selectModificationSupport = selectModificationSupport;
    this.directionBuildDeg = directionBuildDeg;

    this.createLinesInters();
    this.buildGroup(roomsObjects);
  }

  buildGroup(roomsObjects?: RoomObjectSurface) {
    this.createBottomPlaneIntersected();
    this.createRoomsMeshesInters(roomsObjects);
    this.createSupportObjects();
  }
  createBottomPlaneIntersected() {
    const { p0, p1, p2, p3 } = this.getMaxMinPointsContour();
    this.planeIntersBottom = getPlaneIntersected([p0, p1, p2, p3]);
    // this.add(this.planeIntersBottom);
  }
  removeGroup() {
    if (this.planeIntersBottom) {
      this.planeIntersBottom.geometry.dispose();
      (this.planeIntersBottom.material as THREE.MeshBasicMaterial).dispose();
      this.planeIntersBottom = null;
    }
    while (this.planesZeroIntersected.length > 0) {
      this.planesZeroIntersected[0].geometry.dispose();
      (this.planesZeroIntersected[0].material as THREE.MeshBasicMaterial).dispose();
      this.planesZeroIntersected.shift();
    }
    while (this.roomsMeshes.length > 0) {
      this.roomsMeshes[0].geometry.dispose();
      (this.roomsMeshes[0].material as THREE.MeshBasicMaterial).dispose();
      this.roomsMeshes.shift();
    }
    while (this.children.length > 0) {
      (this.children[0] as THREE.Mesh).geometry.dispose();
      ((this.children[0] as THREE.Mesh).material as THREE.MeshBasicMaterial).dispose();
      this.remove(this.children[0]);
    }

    this.parent?.remove(this);
  }

  protected createSupportObjects() {
    if (this.planeIntersBottom) {
      const p3 = (this.planeIntersBottom.geometry as THREE.Geometry).vertices[3].clone();
      const dirZ = new THREE.Vector3(0, 0, -this.offsetZ);
      const dirX = new THREE.Vector3(this.offsetX, 0, 0);
      const pointsLineX: PointIntersType[] = [];
      const pointsLineZ: PointIntersType[] = [];

      let nextPoint = p3
        .add(
          new THREE.Vector3(SupportsGroup.radiusBaseSupport, 0, -SupportsGroup.radiusBaseSupport)
        )
        .setY(0)
        .clone();

      let isRunX = true;
      let isIntersHorizontal = true;
      while (isRunX) {
        let isRunZ = true;

        while (isRunZ && isRunX) {
          SupportsGroup.raycaster.set(nextPoint, SupportsGroup.directBottom);
          const inters = SupportsGroup.raycaster.intersectObject(this.planeIntersBottom);
          isRunZ = inters.length > 0;
          if (isRunZ) {
            SupportsGroup.raycaster.set(nextPoint, SupportsGroup.directTop);
            const intersTop = SupportsGroup.raycaster.intersectObjects(this.roomsMeshes);
            const trackPoint = this.trackEdgeOut(nextPoint);
            let _nextPointTrack;
            if (intersTop.length > 0) {
              if (!trackPoint?.v) {
                _nextPointTrack = nextPoint.clone();
              } //
              else {
                _nextPointTrack = nextPoint
                  .clone()
                  .add(trackPoint.v.multiplyScalar(trackPoint.dist));
              }

              addSupportMesh(this, _nextPointTrack);
              // if (intersTop[0].object instanceof Room) {
              //   addSupportMesh(this, _nextPointTrack);
              // } //
              // else {
              //   addSupportMesh(this, _nextPointTrack);
              // }
            }
          }

          if (isIntersHorizontal) {
            const points = this.getPointsIntersectedLine(nextPoint, dirX.clone().normalize(), 'x');
            pointsLineX.push(...points);
          }
          nextPoint.add(dirZ);
        }
        const points = this.getPointsIntersectedLine(p3, dirZ.clone().normalize(), 'z');
        pointsLineZ.push(...points);
        nextPoint = p3.add(dirX).setY(0).clone();

        SupportsGroup.raycaster.set(nextPoint, SupportsGroup.directBottom);
        const intersect = SupportsGroup.raycaster.intersectObject(this.planeIntersBottom);
        isRunX = intersect.length > 0;
        isIntersHorizontal = false;
      }

      this.addMeshForPointLine(pointsLineX, 'x');
      this.addMeshForPointLine(pointsLineZ, 'z');
      this.arrangementSupportsAlongContour();
    }
  }
  protected createLinesInters() {
    if (this.contour) {
      for (let i = 0; i < this.contour.length; i++) {
        let start, end;
        if (i < this.contour.length - 1) {
          start = this.contour[i].clone().setY(0);
          end = this.contour[i + 1].clone().setY(0);
        } else {
          start = this.contour[i].clone().setY(0);
          end = this.contour[0].clone().setY(0);
        }

        this.linesInters.push({
          start,
          end,
        });
      }
    }
  }
  protected createRoomsMeshesInters(roomsObjects?: RoomObjectSurface) {
    if (roomsObjects && roomsObjects.length > 0) {
      this.roomsMeshes = [...roomsObjects];
      roomsObjects.forEach(item => {
        const dataMeshes = item.getPlanesZeroIntersected();
        if (dataMeshes.length > 0) {
          this.planesZeroIntersected.push(...dataMeshes);
          // dataMeshes.forEach(m => {
          //   this.add(m);
          // });
        }
      });
    } //
    else if (this.rooms.length) {
      this.rooms.forEach(room => {
        const controls = room.map(item => this.contour[item]);
        const plane = getPlaneIntersected(controls, true);
        this.roomsMeshes.push(plane);
      });
    }
  }
  protected getMaxMinPointsContour(): MaxMinPointsProps {
    const pointsX = this.contour.map(point => point.x);
    const pointsZ = this.contour.map(point => point.z);
    const minX = Math.min(...pointsX);
    const minZ = Math.min(...pointsZ);
    const maxX = Math.max(...pointsX);
    const maxZ = Math.max(...pointsZ);
    const p0 = new THREE.Vector3(minX - this.customerOffsetX, 0, minZ - this.offsetZ);
    const p1 = new THREE.Vector3(maxX + this.offsetX, 0, minZ - this.offsetZ);
    const p2 = new THREE.Vector3(maxX + this.offsetX, 0, maxZ + this.customerOffsetZ);
    const p3 = new THREE.Vector3(minX - this.customerOffsetX, 0, maxZ + this.customerOffsetZ);

    return { p0, p1, p2, p3 };
  }
  private getPointNearIntersectedLine(origin: THREE.Vector3, direct: THREE.Vector3): THREE.Vector3 {
    const pointInters = new THREE.Vector3();
    let dist = Infinity;
    for (let i = 0; i < this.linesInters.length; i++) {
      const line = this.linesInters[i];
      const pointIntersLine = new THREE.Vector3();
      SupportsGroup.ray.set(origin, direct);
      const distPointRay = SupportsGroup.ray.distanceSqToSegment(
        line.start,
        line.end,
        undefined,
        pointIntersLine
      );
      // console.log('distPointRay', distPointRay);
      if (distPointRay < 0.00001) {
        const _dist = origin.distanceTo(pointIntersLine);
        if (_dist < dist) {
          pointInters.copy(pointIntersLine);
          dist = _dist;
        }
      }
    }
    return pointInters;
  }
  private getPointsIntersectedLine(
    origin: THREE.Vector3,
    direct: THREE.Vector3,
    sortSymbol: 'x' | 'z'
  ): Array<PointIntersType> {
    const pointInters = [];
    for (let i = 0; i < this.linesInters.length; i++) {
      const line = this.linesInters[i];
      const pointIntersLine = new THREE.Vector3();
      SupportsGroup.ray.set(origin, direct);
      const distPointRay = SupportsGroup.ray.distanceSqToSegment(
        line.start,
        line.end,
        undefined,
        pointIntersLine
      );

      if (distPointRay < 0.00001) {
        const angle = line.end.clone().sub(line.start).normalize().angleTo(direct);
        const gip = SupportsGroup.radiusBaseSupport / Math.sin(angle);
        // console.log(THREE.MathUtils.radToDeg(angle), gip);
        pointInters.push({ point: pointIntersLine, dist: gip });
      }
    }

    if (sortSymbol === 'x') {
      pointInters.sort((a, b) => a.point[sortSymbol] - b.point[sortSymbol]);
    } else {
      pointInters.sort((a, b) => b.point[sortSymbol] - a.point[sortSymbol]);
    }

    return pointInters;
  }
  private trackEdgeOut(position: THREE.Vector3): { v: THREE.Vector3 | null; dist: number } {
    const directions = [];
    let direction = null;
    let dist = 0;
    const dirs = [
      new THREE.Vector3(0, 0, -1),
      new THREE.Vector3(0, 0, 1),
      new THREE.Vector3(-1, 0, 0),
      new THREE.Vector3(1, 0, 0),
    ];
    const offset = SupportsGroup.radiusBaseSupport - 0.001;

    for (let i = 0; i < dirs.length; i++) {
      const origin = position.clone().add(dirs[i].multiplyScalar(offset));
      SupportsGroup.raycaster.set(origin, SupportsGroup.directTop);
      const inters = SupportsGroup.raycaster.intersectObjects(this.roomsMeshes);
      if (inters.length === 0) {
        const dir = dirs[i].clone().negate().normalize();
        directions.push(dir);
        const point = this.getPointNearIntersectedLine(origin, dir);
        // console.log(point.distanceTo(position));
        dist = point.distanceTo(origin);
      }
    }

    if (directions.length === 1) {
      direction = directions[0];
    } else if (directions.length === 2) {
      direction = directions[1].clone().add(directions[0]).normalize();
    }

    return { v: direction, dist };
  }
  private searchFreeSpace(position: THREE.Vector3, height?: number) {
    let isAddingMesh = true;
    for (let i = 0; i < this.children.length; i++) {
      const item = this.children[i];
      const dist = item.position.distanceTo(position);
      if (dist < SupportsGroup.radiusBaseSupport * 2 - 0.03) {
        isAddingMesh = false;
        break;
      }
    }
    if (isAddingMesh) {
      const trackPoint = this.trackEdgeOut(position);
      let _position;
      if (!trackPoint?.v) {
        _position = position.clone();
      } //
      else {
        _position = position.clone().add(trackPoint.v.multiplyScalar(trackPoint.dist));
      }
      addSupportMesh(this, _position);
    }
  }
  private addMeshForPointLine(points: PointIntersType[], direct: 'x' | 'z') {
    const dir = direct === 'x' ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 0, -1);
    points.forEach(p => {
      let origin = p.point.clone().add(dir.clone().multiplyScalar(p.dist));
      SupportsGroup.raycaster.set(origin, SupportsGroup.directTop);
      let intersTop = SupportsGroup.raycaster.intersectObjects(this.roomsMeshes);
      let distance = 0;
      if (intersTop.length === 0) {
        origin = p.point.clone().add(dir.clone().negate().multiplyScalar(p.dist));
        SupportsGroup.raycaster.set(origin, SupportsGroup.directTop);
        intersTop = SupportsGroup.raycaster.intersectObjects(this.roomsMeshes);
        if (intersTop.length > 0) {
          distance = intersTop[0].distance;
        }
      } //
      else {
        distance = intersTop[0].distance;
      }
      this.searchFreeSpace(origin, distance);
      /* const m = new THREE.Mesh(
        new THREE.SphereBufferGeometry(0.03),
        new THREE.MeshBasicMaterial({ color: 'red' })
      );
      m.position.copy(origin);
      Load.scene.add(m);*/
    });
  }
  private arrangementSupportsAlongContour() {
    for (let i = 0; i < this.contour.length; i++) {
      let item;
      let itemNext;
      let itemPrev;

      if (i === 0) {
        itemPrev = this.contour[this.contour.length - 1].clone().setY(0);
        item = this.contour[i].clone().setY(0);
        itemNext = this.contour[i + 1].clone().setY(0);
      } else if (i === this.contour.length - 1) {
        itemPrev = this.contour[i - 1].clone().setY(0);
        item = this.contour[i].clone().setY(0);
        itemNext = this.contour[0].clone().setY(0);
      } else {
        itemPrev = this.contour[i - 1].clone().setY(0);
        item = this.contour[i].clone().setY(0);
        itemNext = this.contour[i + 1].clone().setY(0);
      }
      const dir = itemPrev.clone().sub(item).normalize();
      const dirNext = itemNext.clone().sub(item).normalize();
      const middleVec = dir.clone().add(dirNext).normalize();
      const y = dir.cross(dirNext).getComponent(1);
      if (y < 0) {
        middleVec.negate();
      }

      let distance = 0;
      const nextPoint = item
        .clone()
        .add(middleVec.clone().multiplyScalar(SupportsGroup.radiusBaseSupport));
      SupportsGroup.raycaster.set(nextPoint, SupportsGroup.directTop);
      const intersTop = SupportsGroup.raycaster.intersectObjects(this.roomsMeshes);
      if (intersTop.length > 0) {
        distance = intersTop[0].distance;
      }
      this.searchFreeSpace(nextPoint, distance);
      // visibleDirection(item, middleVec, 1);
    }
  }
}
